import ThemingData from "../content/themingData";
import { calculateLuminance, parseColor } from "./colors";

type ThemingCSS = Record<
  string,
  {
    contentContainer: CSSProperties;
    notificationContainer: CSSProperties;
    emptyContainer: CSSProperties;
    textStyles: TextStyles;
    table: TableStyles;
    cell: CellStyles;
  }
>;

interface CSSProperties {
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  padding?: string;
  borderRadius?: string;
  textAlign?: string;
  textColor?: string;
}

interface TextShadow {
  radius: number;
  opacity: number;
  color: string;
  offset: { x: number; y: number };
}

interface TextStyles extends CSSProperties {
  fontFamily?: string;
  fontSize?: number;
  fontStyle?: string;
  fontWeight?: string;
  fontWidth?: string;
  letterSpacing?: number;
  lineHeight?: number;
  textColor?: string;
  textShadow?: TextShadow;
  textTransform?: string;
}

interface TableStyles extends CSSProperties {
  margin?: string;
  cellSpacing?: string;
  shadow?: string;
  paddingTop?: string;
  paddingBottom?: string;
}

interface CellStyles extends CSSProperties {
  separator?: {
    inset?: string;
    color?: string;
    width?: string;
  };
  shadow?: string;
  paddingBottom?: string;
  paddingTop?: string;
}

const removeUndefinedKeys = (obj: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined && value !== "" && value),
  );

export const readThemingCSS = (result): ThemingCSS | undefined => {
  if (result) {
    try {
      const base64Decoded = atob(result);
      const themingCSS = JSON.parse(base64Decoded) as ThemingCSS;
      return removeUndefinedKeys({ ...ThemingData, ...themingCSS });
    } catch (error) {
      console.error("Error parsing theming CSS from headers:", error);
    }
  }
  return {};
};

export const readThemingCookie = (document) =>
  document.cookie
    ?.split("; ")
    ?.find((row) => row.startsWith("X-Theming-Data="))
    ?.split("=")[1];

export const writeThemeCookie = (themingData) => {
  const jsonThemingData = JSON.stringify(themingData);
  const base64Encoded = btoa(jsonThemingData);
  document.cookie = `X-Theming-Data=${base64Encoded}; path=/`;
};

export const setPrefersDarkTheme = (backgroundColor: string, textColor: string): void => {
  const bgRgb = parseColor(backgroundColor);
  const textRgb = parseColor(textColor);

  if (!bgRgb || !textRgb) {
    return;
  }

  const bgLuminance = calculateLuminance(bgRgb);
  const textLuminance = calculateLuminance(textRgb);

  const isBackgroundDark = bgLuminance < 128;
  const isTextLight = textLuminance > 128;

  if (isBackgroundDark && isTextLight) {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
};
